/**
 * The amenity list the property form renders — single source of truth for WHICH amenities we ask
 * about, how they are grouped, and what each is called in Arabic.
 *
 * tasks/public-amenities-consolidation.md D2/D-E. Mirrors the `roleConfig.js` / `roles.js`
 * convention: a plain config module the UI loops over, so adding an amenity is one line here plus
 * one key in `be/models/Property.js`, not another 18-line copy-pasted checkbox.
 *
 * ── The boundary this file sits on ────────────────────────────────────────────────────────────
 * `amenities` answers DOES THE UNIT HAVE IT. `guidebook.sections` answers HOW THE GUEST USES IT.
 * The guide must never become a second source of truth for existence — otherwise the public tab
 * can say «لا يوجد مسبح» while the guide explains where the pool is, and nothing downstream (the
 * future listings page, the AI assistant) can tell which one to believe.
 *
 * ── Why 19 and not Airbnb's ~40 ───────────────────────────────────────────────────────────────
 * Every entry here is a box a small ops team ticks for every unit, now and forever. 40 × 12 units
 * is ~480 decisions before a single new property. The set below is what our OWN Airbnb and Gathern
 * listings actually advertise (comparison sheet, 2026-08-25) — the things a guest weighs when
 * choosing between apartments. Extensibility lives in the model, not in shipping 40 controls.
 *
 * Bathroom consumables (شامبو، صابون، مناديل، سلبر) are deliberately absent: they change every
 * clean, so they are a cleaning-checklist item, not a fact about the unit.
 *
 * ── Keys are English and stable ───────────────────────────────────────────────────────────────
 * The Arabic string is presentation and may be reworded freely. `path` is the contract — it is
 * what is stored, what a future public page reads, and what any OTA mapping would map FROM. Host
 * Me is the master; Channex and the OTAs are mapping targets, so their taxonomy does not get a
 * vote here (D-G).
 *
 * `path` is dot-notation into `property.amenities`. Most are a bare boolean; the five that carry
 * extra data (a type, an SSID, a location) point at their object's `available` flag and declare a
 * `detail` the form renders underneath.
 */

/** Detail controls rendered under an amenity when it is ticked. Keep this list tiny. */
export const AMENITY_DETAILS = {
  PARKING_TYPE: 'parkingType',
  KITCHEN_TYPE: 'kitchenType'
};

export const AMENITY_GROUPS = [
  {
    key: 'connectivity',
    label: 'الإنترنت والعمل',
    items: [
      // Credentials deliberately stay on `amenities.wifi` for now and the form does not show them
      // here — moving them into the guide is a live-data migration for a tidiness gain (D-H).
      { path: 'wifi.available', label: 'واي فاي' },
      { path: 'workspace', label: 'مساحة عمل' }
    ]
  },
  {
    key: 'kitchen',
    label: 'المطبخ',
    items: [
      { path: 'kitchen.available', label: 'مطبخ', detail: AMENITY_DETAILS.KITCHEN_TYPE },
      { path: 'fridge', label: 'ثلاجة' },
      { path: 'oven', label: 'فرن / موقد' },
      { path: 'microwave', label: 'ميكروويف' },
      { path: 'coffeeMaker', label: 'غلاية / آلة قهوة' }
    ]
  },
  {
    key: 'laundry',
    label: 'الغسيل',
    items: [
      { path: 'washingMachine', label: 'غسالة' },
      { path: 'dryer', label: 'مجفف ملابس' }
    ]
  },
  {
    key: 'comfort',
    label: 'الراحة والترفيه',
    items: [
      { path: 'airConditioning', label: 'تكييف' },
      { path: 'heating', label: 'تدفئة' },
      { path: 'tv', label: 'تلفزيون' },
      { path: 'elevator', label: 'مصعد' }
    ]
  },
  {
    key: 'facilities',
    label: 'المرافق',
    items: [
      { path: 'parking.available', label: 'موقف سيارات', detail: AMENITY_DETAILS.PARKING_TYPE },
      { path: 'pool.available', label: 'مسبح' },
      { path: 'gym.available', label: 'صالة رياضية' },
      { path: 'outdoorSeating', label: 'جلسة خارجية / إطلالة' }
    ]
  },
  {
    key: 'safety',
    label: 'السلامة والدخول',
    items: [
      { path: 'selfCheckIn', label: 'دخول ذاتي' },
      { path: 'smokeDetector', label: 'كاشف دخان' },
      { path: 'fireExtinguisher', label: 'طفاية حريق' }
    ]
  }
];

/** Parking type options — the enum already on the Property schema. */
export const PARKING_TYPES = [
  { value: 'free', label: 'مجاني' },
  { value: 'paid', label: 'مدفوع' },
  { value: 'street', label: 'في الشارع' },
  { value: 'garage', label: 'جراج' }
];

/** Kitchen type options — the enum already on the Property schema. */
export const KITCHEN_TYPES = [
  { value: 'full', label: 'مطبخ كامل' },
  { value: 'kitchenette', label: 'مطبخ صغير' }
];

/** Read a dot-path off an amenities object. Missing anywhere in the chain reads as absent. */
export const readAmenity = (amenities, path) =>
  path.split('.').reduce((node, key) => (node == null ? undefined : node[key]), amenities);

/**
 * Immutably set a dot-path on an amenities object.
 *
 * ⚠️ MERGES, never replaces. `securePropertyRoutes` assigns `property.amenities` wholesale, so a
 * form that rebuilt the object from the catalogue would silently WIPE every key the catalogue does
 * not list — wifi credentials, pool hours, and any amenity added to the schema before its
 * catalogue line. Every write goes through here.
 */
export const writeAmenity = (amenities, path, value) => {
  const [head, ...rest] = path.split('.');
  if (!rest.length) return { ...amenities, [head]: value };
  return {
    ...amenities,
    [head]: writeAmenity(amenities?.[head] || {}, rest.join('.'), value)
  };
};

/** Every path the catalogue offers — used by the guide tab to read availability. */
export const ALL_AMENITY_PATHS = AMENITY_GROUPS.flatMap((g) => g.items.map((i) => i.path));

/**
 * What «نسخ المرافق من وحدة أخرى» is allowed to copy.
 *
 * ⚠️ Availability flags and type selectors ONLY — never the whole `amenities` object. Copying it
 * wholesale would carry the SOURCE unit's WiFi SSID and password, and its pool hours and location,
 * onto this property: wrong data on a guest-facing surface, and a credential leaking between
 * units. Those fields are per-unit facts that no sibling can answer for.
 */
export const COPYABLE_AMENITY_PATHS = [...ALL_AMENITY_PATHS, 'parking.type', 'kitchen.type'];
